"use client";

import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@moontra/moonui";
import { 
  Crown, 
  Sparkles, 
  Circle, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Zap,
  Star,
  Heart,
  Shield,
  Award,
  Gem,
  Flame
} from "lucide-react";

// Avatar variants
const avatarVariants = cva(
  "relative inline-flex shrink-0 overflow-hidden font-semibold transition-all",
  {
    variants: {
      size: {
        xs: "h-6 w-6 text-xs",
        sm: "h-8 w-8 text-xs",
        md: "h-10 w-10 text-sm",
        lg: "h-12 w-12 text-base",
        xl: "h-14 w-14 text-lg",
        "2xl": "h-16 w-16 text-xl",
        "3xl": "h-20 w-20 text-2xl",
      },
      shape: {
        circle: "rounded-full",
        square: "rounded-md",
        hexagon: "mask mask-hexagon",
      },
      ring: {
        none: "",
        default: "ring-2 ring-offset-2 ring-offset-background",
        gradient: "ring-2 ring-offset-2 ring-offset-background",
        glow: "ring-2 ring-offset-2 ring-offset-background shadow-lg",
      }
    },
    defaultVariants: {
      size: "md",
      shape: "circle",
      ring: "none",
    },
  }
);

// Status indicator variants
const statusVariants = cva(
  "absolute rounded-full border-2 border-background",
  {
    variants: {
      size: {
        xs: "h-2 w-2",
        sm: "h-2.5 w-2.5",
        md: "h-3 w-3",
        lg: "h-3.5 w-3.5",
        xl: "h-4 w-4",
        "2xl": "h-5 w-5",
        "3xl": "h-6 w-6",
      },
      position: {
        "bottom-right": "bottom-0 right-0",
        "bottom-left": "bottom-0 left-0",
        "top-right": "top-0 right-0",
        "top-left": "top-0 left-0",
      },
      status: {
        online: "bg-green-500",
        offline: "bg-gray-400",
        busy: "bg-red-500",
        away: "bg-yellow-500",
        focus: "bg-purple-500",
      }
    },
    defaultVariants: {
      size: "md",
      position: "bottom-right",
      status: "offline",
    },
  }
);

// Badge variants
const badgeVariants = cva(
  "absolute flex items-center justify-center rounded-full font-bold text-white",
  {
    variants: {
      size: {
        xs: "h-3 w-3 text-[8px]",
        sm: "h-3.5 w-3.5 text-[9px]",
        md: "h-4 w-4 text-[10px]",
        lg: "h-5 w-5 text-[11px]",
        xl: "h-6 w-6 text-xs",
        "2xl": "h-7 w-7 text-sm",
        "3xl": "h-8 w-8 text-base",
      },
      position: {
        "top-right": "-top-1 -right-1",
        "top-left": "-top-1 -left-1",
        "bottom-right": "-bottom-1 -right-1",
        "bottom-left": "-bottom-1 -left-1",
      },
      variant: {
        default: "bg-primary",
        destructive: "bg-destructive",
        success: "bg-green-500",
        warning: "bg-yellow-500",
        info: "bg-blue-500",
        pro: "bg-gradient-to-r from-purple-500 to-pink-500",
        lifetime: "bg-gradient-to-r from-yellow-400 to-amber-500",
      }
    },
    defaultVariants: {
      size: "md",
      position: "top-right",
      variant: "default",
    },
  }
);

// Icon badge map
const iconBadgeMap = {
  crown: Crown,
  sparkles: Sparkles,
  star: Star,
  heart: Heart,
  shield: Shield,
  award: Award,
  gem: Gem,
  flame: Flame,
  zap: Zap,
};

interface MoonUIAvatarProProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  src?: string;
  alt?: string;
  fallback?: string | React.ReactNode;
  delayMs?: number;
  status?: "online" | "offline" | "busy" | "away" | "focus";
  statusPosition?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  badge?: {
    content?: string | number;
    variant?: VariantProps<typeof badgeVariants>["variant"];
    position?: VariantProps<typeof badgeVariants>["position"];
    icon?: keyof typeof iconBadgeMap;
  };
  ringColor?: string;
  gradientFrom?: string;
  gradientTo?: string;
  animate?: boolean;
  animationType?: "pulse" | "bounce" | "spin" | "ping";
  onStatusClick?: () => void;
  loading?: boolean;
  error?: boolean;
}

const MoonUIAvatarPro = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  MoonUIAvatarProProps
>(({
  className,
  size,
  shape,
  ring,
  src,
  alt,
  fallback,
  delayMs,
  status,
  statusPosition = "bottom-right",
  badge,
  ringColor,
  gradientFrom,
  gradientTo,
  animate = false,
  animationType = "pulse",
  onStatusClick,
  loading = false,
  error = false,
  ...props
}, ref) => {
  const animationClasses = {
    pulse: "animate-pulse",
    bounce: "animate-bounce",
    spin: "animate-spin",
    ping: "animate-ping",
  };

  const ringStyle = React.useMemo(() => {
    if (ring === "gradient" && gradientFrom && gradientTo) {
      return {
        background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
        padding: "2px",
      };
    }
    if (ringColor) {
      return {
        boxShadow: `0 0 0 2px ${ringColor}`,
      };
    }
    return {};
  }, [ring, ringColor, gradientFrom, gradientTo]);

  const BadgeIcon = badge?.icon ? iconBadgeMap[badge.icon] : null;

  return (
    <div className="relative inline-block">
      {/* Gradient ring wrapper */}
      {ring === "gradient" && (
        <div
          className="absolute -inset-[2px] rounded-full"
          style={{
            background: `linear-gradient(135deg, ${gradientFrom || "#8b5cf6"}, ${gradientTo || "#ec4899"})`,
          }}
        />
      )}

      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          avatarVariants({ size, shape, ring }),
          animate && animationClasses[animationType],
          ring === "gradient" && "relative",
          className
        )}
        style={ring !== "gradient" ? ringStyle : undefined}
        {...props}
      >
        <AvatarPrimitive.Image
          src={src}
          alt={alt}
          className="aspect-square h-full w-full object-cover"
        />
        <AvatarPrimitive.Fallback
          className={cn(
            "flex h-full w-full items-center justify-center bg-muted",
            loading && "animate-pulse",
            error && "bg-destructive/10 text-destructive"
          )}
          delayMs={delayMs}
        >
          {loading ? (
            <div className="h-1/2 w-1/2 rounded-full bg-muted-foreground/20 animate-pulse" />
          ) : error ? (
            <XCircle className="h-1/2 w-1/2" />
          ) : (
            fallback
          )}
        </AvatarPrimitive.Fallback>
      </AvatarPrimitive.Root>

      {/* Status indicator */}
      {status && !loading && (
        <button
          className={cn(
            statusVariants({ size, position: statusPosition, status }),
            onStatusClick && "cursor-pointer hover:scale-110 transition-transform",
            status === "online" && "animate-pulse"
          )}
          onClick={onStatusClick}
          aria-label={`Status: ${status}`}
        />
      )}

      {/* Badge */}
      {badge && !loading && (
        <div
          className={cn(
            badgeVariants({
              size,
              position: badge.position,
              variant: badge.variant,
            }),
            "shadow-sm"
          )}
        >
          {BadgeIcon ? (
            <BadgeIcon className="h-full w-full p-0.5" />
          ) : (
            badge.content
          )}
        </div>
      )}
    </div>
  );
});

MoonUIAvatarPro.displayName = "MoonUIAvatarPro";

// Avatar Group Component
interface MoonUIAvatarGroupProProps {
  children: React.ReactNode;
  max?: number;
  size?: VariantProps<typeof avatarVariants>["size"];
  spacing?: "tight" | "normal" | "loose";
  className?: string;
}

const MoonUIAvatarGroupPro = React.forwardRef<
  HTMLDivElement,
  MoonUIAvatarGroupProProps
>(({ children, max = 4, size = "md", spacing = "normal", className }, ref) => {
  const childrenArray = React.Children.toArray(children);
  const visibleChildren = childrenArray.slice(0, max);
  const remainingCount = childrenArray.length - max;

  const spacingClasses = {
    tight: "-space-x-3",
    normal: "-space-x-2",
    loose: "-space-x-1",
  };

  return (
    <div
      ref={ref}
      className={cn("flex items-center", spacingClasses[spacing], className)}
    >
      {visibleChildren.map((child, index) => (
        <div
          key={index}
          className="relative ring-2 ring-background rounded-full"
          style={{ zIndex: visibleChildren.length - index }}
        >
          {child}
        </div>
      ))}
      {remainingCount > 0 && (
        <div
          className={cn(
            avatarVariants({ size, shape: "circle" }),
            "relative flex items-center justify-center bg-muted ring-2 ring-background",
            "text-muted-foreground font-medium"
          )}
          style={{ zIndex: 0 }}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
});

MoonUIAvatarGroupPro.displayName = "MoonUIAvatarGroupPro";

// Async Avatar Component
interface MoonUIAsyncAvatarProProps extends MoonUIAvatarProProps {
  userId?: string;
  fetchUser?: (userId: string) => Promise<{ name?: string; image?: string }>;
}

const MoonUIAsyncAvatarPro = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  MoonUIAsyncAvatarProProps
>(({ userId, fetchUser, ...props }, ref) => {
  const [userData, setUserData] = React.useState<{
    name?: string;
    image?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    if (userId && fetchUser) {
      setIsLoading(true);
      setHasError(false);
      fetchUser(userId)
        .then(setUserData)
        .catch(() => setHasError(true))
        .finally(() => setIsLoading(false));
    }
  }, [userId, fetchUser]);

  return (
    <MoonUIAvatarPro
      ref={ref}
      src={userData?.image || props.src}
      fallback={userData?.name?.[0] || props.fallback}
      loading={isLoading}
      error={hasError}
      {...props}
    />
  );
});

MoonUIAsyncAvatarPro.displayName = "MoonUIAsyncAvatarPro";

export {
  MoonUIAvatarPro,
  MoonUIAvatarGroupPro,
  MoonUIAsyncAvatarPro,
  avatarVariants,
  statusVariants,
  badgeVariants,
};

export type {
  MoonUIAvatarProProps,
  MoonUIAvatarGroupProProps,
  MoonUIAsyncAvatarProProps,
};